import { CSVLink } from 'react-csv';
import React, { useState, useRef, useEffect } from 'react';
import { Button, Stack, Icon, useToast } from '@pairy/pds-react';
import { useAtomValue } from 'jotai';

import { userInfoAtom } from '@/state/user/atom';
import ProPlanUpgradeModal from '@/components/User/_components/common/ProPlanUpgradeModal';

interface Props {
  className?: string;
  filename: string;
  csvData?: Record<string, any>[];
  isLoading?: boolean;
  onClick?: () => void | Promise<void>;
}

const CSVDownloadButton = ({
  className,
  filename,
  csvData,
  isLoading,
  onClick,
  children,
}: React.PropsWithChildren<Props>) => {
  const toast = useToast();
  const userInfo = useAtomValue(userInfoAtom);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const pendingDownload = useRef(false);

  const csvLinkRef = React.useRef<
    CSVLink & HTMLAnchorElement & { link: HTMLAnchorElement }
  >(null);

  useEffect(() => {
    if (
      pendingDownload.current &&
      !isLoading &&
      csvData &&
      csvData.length > 0
    ) {
      pendingDownload.current = false;
      csvLinkRef.current?.link.click();
    }
  }, [isLoading, csvData]);

  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (userInfo?.subscriptionPlan?.name === 'Free') {
      toast('해당 기능은 Basic 플랜부터 이용할 수 있습니다');
      setIsUpgradeModalOpen(true);
      return;
    }
    if (isLoading) {
      return;
    }
    if (onClick) {
      pendingDownload.current = true;
      await onClick();
      return;
    }
    csvLinkRef.current?.link.click();
  };

  return (
    <>
      <CSVLink
        ref={csvLinkRef}
        filename={`${filename}.csv`}
        data={csvData ?? []}
        style={{ display: 'none' }}
      />
      <Button
        className={className}
        variant="zinc_200"
        textVariant="md-medium"
        radius={9999}
        onClick={handleClick}
        disabled={isLoading}
      >
        <Stack gap={4}>
          <Icon
            icon={isLoading ? 'ph:spinner' : 'ph:download-simple'}
            size={17}
          />
          {children ?? '열람 기록 다운로드 (CSV)'}
        </Stack>
      </Button>
      {isUpgradeModalOpen && (
        <ProPlanUpgradeModal onOpenChange={setIsUpgradeModalOpen} />
      )}
    </>
  );
};

export default CSVDownloadButton;
